/**
 * @fileoverview The one line each industry page owes a reader about plans.
 *
 * These pages describe capabilities the free plan does not have. Four of the
 * five are gated in the product with a lock and a "Professional-plan feature"
 * tooltip (premiumColumns.js, CostSettings.jsx, OptimizeButton's multi-day
 * check); the fifth is enforced by the backend on solve. None of that was said
 * on the pages themselves, and three of them take paid advertising traffic — so
 * someone could arrive on a bought click, read a page whose central promise is
 * mid-route reloads, sign up free, and discover the promise is locked.
 *
 * The fix had to be light. Nine pages tagged "(Professional)" on every sentence
 * would be its own problem, so this is one derived line per page, under the
 * constraints section where the claims are densest.
 *
 * **Derived, not written down.** Each industry declares which capabilities its
 * copy leans on (`professionalCapabilities` in industriesData.js). Which of
 * those the free plan actually lacks is read from the catalog, so the day the
 * backend grants free a capability, it drops out of this sentence with nobody
 * editing a string. That is the same failure the pricing header had: a true
 * sentence that the catalog moved out from under.
 *
 * **It fails the opposite way to the feature gates, on purpose.** A gate fails
 * open — an unknown plan locks nothing, because wrongly locking a paying
 * customer is the harm there. Disclosure is the mirror image: the harm is a
 * reader who is not told, so when the catalog cannot answer this still says
 * something, just without naming capabilities it can no longer vouch for.
 * Uncertainty must not quietly restore the misleading page.
 */

import { findPlanInCatalog } from 'functions/entitlements/featureEntitlements';

/**
 * How each capability is named to a reader.
 *
 * Deliberately the product's own words for these — the lock tooltips say
 * "Road-network distances", "Sending a vehicle back to reload mid-route" and
 * "Locking a stop to a specific vehicle" — so the page and the thing it
 * describes do not use two vocabularies for one feature.
 */
const CAPABILITY_PROSE = {
  road_network_costs: 'road-network distances',
  multi_day: 'multi-day plans',
  resupply: 'mid-route reloads',
  heterogeneous_travel_costs: 'travel costs for each vehicle type',
  fixed_assignments: 'pinning a stop to a vehicle',
};

/*
Which of those phrases take a singular verb. Agreement follows the PHRASE, not
how many capabilities are listed: one item can be either ("mid-route reloads
are", "pinning a stop to a vehicle is"), while any list of two or more is
plural. Deriving it from the count alone produced "Mid-route reloads is a
Professional-plan feature."
*/
const SINGULAR_PHRASES = new Set(['fixed_assignments']);

/**
 * Said when the page leans on something premium but the catalog cannot say what.
 * Names no capability, so it cannot go stale, and makes no claim the page's own
 * declaration does not already make.
 */
export const PLAN_NOTE_FALLBACK =
  'Some of the capabilities on this page are Professional-plan features.';

/*
Worth saying, and true on every plan: a published example solves under that
example's own allowance rather than the visitor's tier, so the examples linked
from this page really do run for a signed-out visitor and on the free plan. The
limit applies to a scenario someone builds themselves. (submit_problem sends the
example's slug; the backend grants the `example` tier, which inherits
professional. OptimizeButton's multi-day check exempts an example for the same
reason.)
*/
const EXAMPLES_CAVEAT = 'The examples on this page run on any plan; the limits apply to scenarios you build yourself.';

/** "a", "a and b", "a, b and c" — no serial comma, matching the site's voice. */
function listSentence(items) {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Build the plan note for one industry page.
 *
 * @param {Array<object>|null|undefined} catalog Items from GET /plans.
 * @param {Array<string>|null|undefined} declared The capability keys this page's
 *   copy leans on, from `industry.professionalCapabilities`.
 * @returns {string|null} The line to render, or null when the page claims
 *   nothing premium and so owes the reader nothing.
 */
export function buildIndustryPlanNote(catalog, declared) {
  const keys = Array.isArray(declared) ? declared.filter((key) => CAPABILITY_PROSE[key]) : [];
  if (keys.length === 0) return null;

  const free = findPlanInCatalog(catalog, 'free');
  const features = free && free.features;
  if (!features || typeof features !== 'object') return `${PLAN_NOTE_FALLBACK} ${EXAMPLES_CAVEAT}`;

  // Only an explicit `false` withholds, matching isFeatureAllowed. A key the
  // catalog does not mention is not evidence of a lock, so it is not claimed.
  const keysWithheld = keys.filter((key) => features[key] === false);
  if (keysWithheld.length === 0) return null;
  const withheld = keysWithheld.map((key) => CAPABILITY_PROSE[key]);

  const singular = keysWithheld.length === 1 && SINGULAR_PHRASES.has(keysWithheld[0]);
  const subject = capitalize(listSentence(withheld));
  const verb = singular ? 'is a Professional-plan feature' : 'are Professional-plan features';
  return `${subject} ${verb}. ${EXAMPLES_CAVEAT}`;
}

export default buildIndustryPlanNote;
